import { inject, bindable, BindingEngine, TaskQueue } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { DialogController } from 'aurelia-dialog';
import { DialogService } from 'aurelia-dialog';
import { QuoteService } from 'pages/quotes/service';
import { BindingSignaler } from 'aurelia-templating-resources';
import { calculateAllToFloat } from 'resources/utility';
import moment from 'moment';
import {Router} from 'aurelia-router';

@inject(DialogController, EventAggregator, DialogService, QuoteService, BindingSignaler, Router, BindingEngine, TaskQueue)
export class QuoteStructureEdit {
    @bindable quoteStructures;
    @bindable quote;

    constructor(controller, ea, dialogService, quoteService, bindingSignaler, router, bindingEngine, taskQueue) {
        this.controller = controller;
        this.ea = ea;
        this.dialogService = dialogService;
        this.quoteService = quoteService;
        this.showEdit = false;
        this.signaler = bindingSignaler;
        this.router = router;
        this.bindingEngine = bindingEngine;
        this.taskQueue = taskQueue;

        this.totalRawMaterial = 0.0;
        this.totalVariance = 0.0;
        this.totalWeld = 0.0;
        this.totalPress = 0.0;
        this.totalPurchase = 0.0;
        this.totalOther = 0.0;
        this.totalSubtotal = 0.0;
        this.totalSGA = 0.0;
        this.totalProfit = 0.0;
        this.totalsSum = 0.0;
        this.totalPiecePrice = 0.0;

        this.ea.subscribe('quote:reload', () => {
          this.taskQueue.queueMicroTask(() => {
            this.loadStructures();
          });
        });


    }

    loadStructures = () => {
      if (this.quoteStructures == undefined) {
        this.quoteStructures = [];
      }
      this.quoteStructures.map(item => {
        const quantity = item.quantity * this.getParentQuantityRecursive(item.parentQuoteId);

        item.sumWeld = item.weld * quantity;
        item.sumPress = item.press * quantity;
        item.sumOther = item.other * quantity;
        item.sumPurchase = (item.purchasePrice * quantity).toFixed(4);
        item.sumRawMaterial = item.rawMaterialNetPrice * quantity;
        item.sumVariance = item.rawMaterialVariance * quantity;
        item.overridePurchase = item.sumPurchase;
        item.subtotal = this.getSubTotalForStructure(item);
        this.calculateSgaAndProfit(item, true);
        item.total = this.getTotalForStructure(item);
      });
      this.changeTotal();
    }

    getParentQuantityRecursive(quoteId) {
      const parent = quoteId != null ? this.quoteStructures.find(x => x.quoteId == quoteId) : null;
      if (parent == null) {
        return this.quoteStructures.find(x => x.parentQuoteId == null).quantity;
      }
      return parent.quantity * this.getParentQuantityRecursive(parent.parentQuoteId);
    }

    attached() {
      this.loadStructures();
      //Adjust the first record based off changes that may have happened in other tabs
      this.saveSubscription = this.ea.subscribe('show-section:change', () => {
        this.changeTotal();
      });

      this.name = window.localStorage.getItem('name');
      if ((this.quote.lockedAt != null && this.quote.lockedBy.toLowerCase() != this.name.toLowerCase() && moment().utc().diff(moment(this.quote.lockedAt), 'minutes') < 20) || this.quote.isSnapshot) {
        this.isReadonly = true;
      }
    }
    
    changeTotal() {
      this.getItemTotals();
      this.getTotalPiecePrice();
      this.signaler.signal('change-total');
    }

    changePurchasePrice(item) {
      item.purchasedPriceOverride = item.overridePurchase / item.quantity;
      this.signaler.signal('change-total');
    }

    changeSga(item) {
      item.sgaChanged = true;
      this.getItemTotals();
      this.getTotalPiecePrice();
      this.signaler.signal('change-total');
      if (item.overrideSga == null || item.overrideSga.length == 0) {
        item.overrideSga = item.calcSga;
        item.sga = item.calcSga;
        item.sgaChanged = false;
        item.sgaOverride = null;
      } else {
        item.sga = item.overrideSga;
        item.sgaOverride = item.overrideSga;
      }
    }

    changeProfit(item) {
      item.profitChanged = true;
      this.getItemTotals();
      this.getTotalPiecePrice();
      this.signaler.signal('change-total');
      if (item.overrideProfit == null || item.overrideProfit.length == 0) {
        item.overrideProfit = item.calcProfit;
        item.profit = item.calcProfit;
        item.profitChanged = false;
        item.profitOverride = null;
      } else {
        item.profit = item.overrideProfit;
        item.profitOverride = item.overrideProfit;
      }
    }

    getItemTotals() { 
      this.quoteStructures.map(item => {
        item.subtotal = this.getSubTotalForStructure(item);
        this.calculateSgaAndProfit(item);
        item.total = this.getTotalForStructure(item);
      }); 
      this.totalRawMaterial = this.quoteStructures.map(item => item.sumRawMaterial).reduce((prev, next) => Number(prev) + Number(next));
      this.totalVariance = this.quoteStructures.map(item => item.sumVariance).reduce((prev, next) => Number(prev) + Number(next));
      this.totalWeld = this.quoteStructures.map(item => item.sumWeld).reduce((prev, next) => Number(prev) + Number(next));
      this.totalPress = this.quoteStructures.map(item => item.sumPress).reduce((prev, next) => Number(prev) + Number(next));
      this.totalPurchase = this.quoteStructures.map(item => item.overridePurchase).reduce((prev, next) => Number(prev) + Number(next));
      this.totalOther = this.quoteStructures.map(item => item.sumOther).reduce((prev, next) => Number(prev) + Number(next));
      this.totalSubtotal = this.quoteStructures.map(item => item.subtotal).reduce((prev, next) => Number(prev) + Number(next));
      this.totalSGA = this.quoteStructures.map(item => item.calcSga).reduce((prev, next) => Number(prev) + Number(next));
      this.totalProfit = this.quoteStructures.map(item => item.calcProfit).reduce((prev, next) => Number(prev) + Number(next));
      this.totalsSum = this.quoteStructures.map(item => item.total).reduce((prev, next) => Number(prev) + Number(next));
    } 

    getTotalPiecePrice() {
      this.totalCalculated = calculateAllToFloat(this.quote.otherAllowance, this.totalsSum, this.quote.packagingAllowance, this.quote.freightAllowance);
      this.quote.totalPiecePrice = this.totalCalculated;
    }

    getSubTotalForStructure(item) {
      return calculateAllToFloat(item.overridePurchase, item.sumWeld, item.sumPress, item.sumOther, item.sumRawMaterial);
    }

    getTotalForStructure(item) {
      return calculateAllToFloat(item.subtotal, item.overrideSga, item.overrideProfit, item.sumVariance);      
    }

    calculateSgaAndProfit(item, firstRun = false) {
      let sgaPercent = this.quote.sgaPercent;
      let profitPercent = this.quote.profitPercent;
      let newCalcSga = Number(item.subtotal) * (sgaPercent / 100);
      let newCalcProfit = (Number(item.subtotal) + Number(newCalcSga)) * (profitPercent / 100);
      if (item.overrideSga != newCalcSga && !item.sgaChanged && !item.ignoreSGA) {
        item.overrideSga = newCalcSga;
      }
      if (item.overrideProfit != newCalcProfit && !item.profitChanged && !item.ignoreProfit) {
        item.overrideProfit = newCalcProfit;
      }

      if (item.overrideSga != null && item.overrideSga > 0) {
        newCalcSga = item.overrideSga;
      }

      if (item.overrideProfit != null && item.overrideProfit > 0) {
        newCalcProfit = item.overrideProfit;
      }

      if (firstRun) {
        if (item.sgaChanged) item.overrideSga = item.sga * item.quantity;
        if (item.profitChanged) item.overrideProfit = item.profit * item.quantity;
      }

      if (item.ignoreSGA) {
        newCalcSga = 0;
      }

      if (item.ignoreProfit) {
        newCalcProfit = 0;
      }


      item.calcSga = newCalcSga;
      item.calcProfit = newCalcProfit;
    }

    editQuote(id) {
      if (this.quote.isSnapshot) {
        this.childQuoteWindow = window.open(this.router.generate('quote-edit', { id: id, isChild: true, snapshotIdentifier: this.quote.snapshotIdentifier }), '_blank');
      } else {
        this.childQuoteWindow = window.open(this.router.generate('quote-edit', { id: id, isChild: true }), '_blank');
      }
    }
    
    async addQuote(item) {
      let quoteItem = {
        partId: item.partId,
        partNumber: item.partNumber,
        plantName: this.quote.plantName,
        plantId: this.quote.plantId,
        customerProjectId: this.quote.customerProjectId,
        customerId: this.quote.customerId,
        customerName: this.quote.customerName,
        projectId: this.quote.projectId,
        projectNumber: this.quote.projectNumber,
        quoteStructureId: item.id,
        volumePerMonth: this.quote.volumePerMonth,
        parentQuoteId: this.quote.id,
        structureId: item.id
      };

      const quotesFromParts = await this.quoteService.getQuotesByPart(item.partId);
      if (quotesFromParts != null && quotesFromParts.length > 0) {
        this.dialogService.open({ viewModel: this.quoteService.linkChildQuoteEditor, model: { quotes: quotesFromParts, item: quoteItem } }).whenClosed(response => {
          if (!response.wasCancelled) {
            item.quoteId = response.output;
            this.ea.publish('quote:save');
          }
        });
        return;
      }

      this.dialogService.open({ viewModel: this.quoteService.add, model: quoteItem });
    }

    async loadAllowances() {      
      const allowances = await this.quoteService.getAllowancesById(this.quote.id);
      this.quote.otherAllowance = allowances.otherAllowance;
      this.quote.freightAllowance = allowances.freightAllowance;
      this.quote.packagingAllowance = allowances.packagingAllowance;
      this.changeTotal();
    }

}
